import useNotifications from '@/composables/useNotifications'
import { useRouter } from '@core/utils/utils'
import { ref } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useCommon from "@/views/organization/useCommon"

export default function useLocationEdit() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { route, router } = useRouter()
  const { getItem, updateItem, getItems } = useLambda()
  const { assignSupervisorsBasedOnDepartment } = useCommon()
  const location = ref({})
  const isSubmitting = ref(false)
  const locationId = route.value.params.id
  const query = { _id: oid(locationId) }
  const collection = 'location'

  const show = async () => {
    isSubmitting.value = true

    try {
      const item = await getItem({ collection, query })
      if (!item) throw new Error('Item not found')

      const leadsOptions = await getItems({ collection: 'worker', query: { _id: { $in: item.departmentLeads?.map(id => oid(id)) || [] } }})
      item.departmentLeads = leadsOptions.map(({ _id, name }) => ({ value: _id.toString(), title: name }))
      item.dailyDialogueDays = item.dailyDialogueDays || [1, 2, 3, 4, 5, 6, 0]
      item.dailyDialoguesCount = item.dailyDialoguesCount
      location.value = item
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.location_fetch_error'))
    } finally {
      isSubmitting.value = false
    }
  }

  const update = async data => {
    isSubmitting.value = true

    const departmentLeads = Array.isArray(data.departmentLeads?.value)
      ? data.departmentLeads?.value?.map(e => typeof e === 'string' ? e : e.value)
      : []

    const payload = {
      location: data.location?.value || '',
      area: data.area?.value ?  oid(data.area?.value)  : null,
      physical_location: data.physical_location?.value ? oid(data.physical_location.value) : null,
      departmentLeads: departmentLeads.map(e => oid(e)),
      dailyDialogueDays: data.dailyDialogueDays.value,
      dailyDialoguesCount: data.dailyDialoguesCount?.value,
    }

    if (data.zone?.value) payload.zone = data.zone.value
    if (data.agency?.value) payload.agency = data.agency.value

    try {
      const action = { $set: payload }

      await updateItem({ collection, query, action })

      // Update supervisors of the workers that have this department based on the selected department leads
      assignSupervisorsBasedOnDepartment([locationId], departmentLeads)

      showSuccessMessage(i18n.t('message.location_updated'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.location_update_error'))
    } finally {
      router.push({ name: 'organization-location-list' })
      isSubmitting.value = false
    }
  }

  return {
    update,
    show,
    location,
    isSubmitting,
  }
}
